import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import axios from 'axios';
import { HEADERS } from '../../../../../API/UrlAndPaths';
import Swal from 'sweetalert2';
import "./payrollLeaveStyles.css";
import Pagelayout from '../../components/Pagelayout';
import { useNavigate } from 'react-router-dom';
const CreatePayrollLeave = () => {
  const navigate = useNavigate();
  const userMeta = localStorage.getItem('login_meta');
  const userBranchId = JSON.parse(userMeta)?.branchId;
  
  // State
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [reason, setReason] = useState('');
  const [leaveDays, setLeaveDays] = useState(0);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  
  const handleBackBtn = () => {
    navigate("/userdashboard/payroll/attendance/payrollLeave");
  };
  
  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get('employee/getAllEmployees', HEADERS);
        console.log('Employees Response:', res);
        if (res.status === 200) {
          const employeeData = res.data.data || res.data || [];
          console.log('Setting employees:', employeeData);
          setEmployees(employeeData);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);
  
  // Fetch leave types
  useEffect(() => {
    const fetchLeaveTypes = async () => {
      try {
        const res = await axios.get('employee/viewLeaveType', HEADERS);
        console.log('Leave Types Response:', res);
        if (res.status === 200) {
          const leaveTypeData = res.data.data || res.data || [];
          console.log('Setting leave types:', leaveTypeData);
          setLeaveTypes(leaveTypeData);
        }
      } catch (error) {
        console.error('Error fetching leave types:', error);
      }
    };
    fetchLeaveTypes();
  }, []);
  
  // Calculate leave days
  useEffect(() => {
    if (fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setLeaveDays(diffDays);
    } else {
      setLeaveDays(0);
    }
  }, [fromDate, toDate]);
  
  // Fetch leave balance when employee and leave type selected
  useEffect(() => {
    const fetchBalance = async () => {
      if (selectedEmployee && selectedLeaveType) {
        setBalanceLoading(true);
        try {
          const res = await axios.get(
            `payroll/leave/balance/${selectedEmployee._id}?year=${new Date().getFullYear()}`,
            HEADERS
          );
          if (res.status === 200) {
            const balance = res.data.balances?.find(
              b => b.leaveTypeId?._id === selectedLeaveType._id
            );
            setLeaveBalance(balance || null);
          }
        } catch (error) {
          console.error('Error fetching balance:', error);
          setLeaveBalance(null);
        } finally {
          setBalanceLoading(false);
        }
      }
    };
    fetchBalance();
  }, [selectedEmployee, selectedLeaveType]);
  
  // Handle submit
  const handleApplyBtn = async () => {
    // Validation
    if (!selectedEmployee) {
      Swal.fire('Error', 'Please select an employee', 'error');
      return;
    }
    if (!selectedLeaveType) {
      Swal.fire('Error', 'Please select a leave type', 'error');
      return;
    }
    if (!fromDate || !toDate) {
      Swal.fire('Error', 'Please select from and to dates', 'error');
      return;
    }
    if (!reason.trim()) {
      Swal.fire('Error', 'Please enter a reason', 'error');
      return;
    }
    
    // Check balance
    if (leaveBalance && leaveBalance.available < leaveDays) {
      Swal.fire(
        'Insufficient Balance',
        `Employee has only ${leaveBalance.available} days available, but requesting ${leaveDays} days`,
        'error'
      );
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(
        'employee/staffLeave',
        {
          emp_id: selectedEmployee._id,
          leaveType: selectedLeaveType._id,
          leaveFromeDate: fromDate,
          leaveToDate: toDate,
          reason: reason,
          branchId: userBranchId,
          appliedDate: new Date().toISOString().split('T')[0],
        },
        HEADERS
      );
      
      if (res.status === 200) {
        Swal.fire('Success', 'Leave application created successfully', 'success');
        navigate('/userdashboard/payroll/attendance/payrollLeave');
      }
    } catch (error) {
      console.error('Error creating leave:', error);
      Swal.fire(
        'Error',
        error.response?.data?.msg || 'Failed to create leave application',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/userdashboard/payroll/attendance/payrollLeave');
  }
  return (
    <Pagelayout
      pageName={"Leave"}
      headerLayout={2}
      handleBack={handleBackBtn}
      actionBtn={false}
      
    >
      <Paper className="payroll-leave-cmnt">
        <Box p={2} className="payroll-leave-cmnt__form">
          {/* Action Buttons at the Top */}
          <Stack direction="row" spacing={2} mb={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              className="payroll-leave-cmnt__apply-btn"
              onClick={handleApplyBtn}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Apply Leave'}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              className="payroll-leave-cmnt__cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
          <Divider />

          <Stack spacing={3} mt={2}>
            {/* First Row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={employees}
                getOptionLabel={(option) => `${option.staff_name} (EMP${option.emp_id})`}
                value={selectedEmployee}
                onChange={(e, newValue) => setSelectedEmployee(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee"
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                )}
              />

              <Autocomplete
                fullWidth
                size="small"
                options={leaveTypes}
                getOptionLabel={(option) => `${option.leaveType} (${option.code || ''})`}
                value={selectedLeaveType}
                onChange={(e, newValue) => setSelectedLeaveType(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Leave Type"
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                )}
              />
            </Stack>

            {/* Second Row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />

              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate }}
                required
              />

              <TextField
                fullWidth
                size="small"
                label="No of Days"
                value={leaveDays}
                InputLabelProps={{ shrink: true }}
                disabled
              />
            </Stack>

            {/* Reason Field */}
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />

            {/* Leave Balance Card */}
            {selectedEmployee && selectedLeaveType && (
              <Paper variant="outlined" className="payroll-leave-cmnt__summary">
                <Box p={2}>
                  <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                    Leave Balance
                  </Typography>
                  {balanceLoading ? (
                    <CircularProgress size={24} />
                  ) : leaveBalance ? (
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Allocated:</Typography>
                        <Chip label={leaveBalance.allocated} size="small" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Used:</Typography>
                        <Chip label={leaveBalance.used} size="small" color="error" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Pending:</Typography>
                        <Chip label={leaveBalance.pending} size="small" color="warning" />
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography>Available:</Typography>
                        <Chip 
                          label={leaveBalance.available} 
                          size="small" 
                          color={leaveBalance.available >= leaveDays ? "success" : "error"}
                        />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      {leaveDays > 0 && (
                        <Box>
                          <Typography variant="body2">
                            Requesting: <strong>{leaveDays} days</strong>
                          </Typography>
                          {leaveBalance.available < leaveDays && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              Insufficient balance! Only {leaveBalance.available} days available.
                            </Alert>
                          )}
                        </Box>
                      )}
                    </Stack>
                  ) : (
                    <Alert severity="warning">
                      No leave balance found for this employee and leave type.
                      Please allocate leaves first.
                    </Alert>
                  )}
                </Box>
              </Paper>
            )}
          </Stack>
        </Box>
      </Paper>
    </Pagelayout>
  );
};

export default CreatePayrollLeave;
